import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSyncAlt, FaTimes, FaCheck, FaPlus } from 'react-icons/fa';

/**
 * MoveInventoryModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - sourceRow: inventory row (from /api/inventory/by-receipt/:id)
 * - apiBase: string (import.meta.env.VITE_API_URL)
 * - onMoved: () => void  // callback after a successful move to refresh parent list
 *
 * What it does:
 * - Lets user choose how many units/items to move from the source row.
 * - Pick a destination Receipt ID and Pallet # (from that receipt’s pallets).
 * - Calls POST `${apiBase}/api/inventory/move` (implement backend to perform the move).
 */
export default function MoveInventoryModal({
  isOpen,
  onClose,
  sourceRow,
  apiBase,
  onMoved,
}) {
  // ----- Movable + Resizable state -----
  const STATE_KEY = 'moveInvModal.state.v1';
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const clampState = (saved) => {
    const vpW = window.innerWidth || 1280;
    const vpH = window.innerHeight || 800;
    const minW = 720, minH = 440, maxW = Math.max(minW, vpW - 20), maxH = Math.max(minH, vpH - 20);
    const width = clamp(Number(saved?.width ?? Math.min(980, vpW - 40)), minW, maxW);
    const height = clamp(Number(saved?.height ?? Math.min(660, vpH - 40)), minH, maxH);
    const maxX = Math.max(0, vpW - width);
    const maxY = Math.max(0, vpH - height);
    const x = clamp(Number(saved?.x ?? Math.round((vpW - width) / 2)), 0, maxX);
    const y = clamp(Number(saved?.y ?? Math.round((vpH - height) / 2)), 0, maxY);
    return { width, height, x, y };
  };
  const [size, setSize] = useState({ width: 980, height: 660 });
  const [position, setPosition] = useState({ x: 80, y: 80 });
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const s = clampState(raw ? JSON.parse(raw) : {});
      setSize({ width: s.width, height: s.height });
      setPosition({ x: s.x, y: s.y });
    } catch {}
    hydratedRef.current = true;
  }, [isOpen]);
  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STATE_KEY, JSON.stringify({ ...size, ...position })); } catch {}
  }, [size, position]);

  // Dragging
  const startDragging = (e) => {
    // Avoid starting drag when clicking header buttons/inputs
    if (e.target && typeof e.target.closest === 'function' && e.target.closest('button, input, select, textarea, a')) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const init = { ...position };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const vpW = window.innerWidth, vpH = window.innerHeight;
      const maxX = Math.max(0, vpW - size.width);
      const maxY = Math.max(0, vpH - size.height);
      const x = clamp(init.x + dx, 0, maxX);
      const y = clamp(init.y + dy, 0, maxY);
      setPosition({ x, y });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp, { once: true });
  };

  // Resizing (bottom-right gripper)
  const startResizing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const init = { ...size };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const vpW = window.innerWidth, vpH = window.innerHeight;
      const minW = 720, minH = 440;
      const width = clamp(init.width + dx, minW, Math.max(minW, vpW - position.x - 10));
      const height = clamp(init.height + dy, minH, Math.max(minH, vpH - position.y - 10));
      setSize({ width, height });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp, { once: true });
  };

  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  // quantities to move
  // quantities to move
  // storage units (unopened) and loose items
  const [moveCases, setMoveCases] = useState(0);
  const [moveLooseItems, setMoveLooseItems] = useState(0);

  // destination
  const [destReceiptId, setDestReceiptId] = useState('');
  const [destPalletOptions, setDestPalletOptions] = useState([]);
  const [destPalletNumber, setDestPalletNumber] = useState('');
  const [destLoading, setDestLoading] = useState(false);
  // Track the exact clicked row for clearer highlighting (in case multiple rows share the pallet #)
  const [selectedDestRowIdx, setSelectedDestRowIdx] = useState(0);

  // Available Locations (by Warehouse Area)
  const [destWarehouseId, setDestWarehouseId] = useState(null);
  const [areaOptions, setAreaOptions] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [areaLocsLoading, setAreaLocsLoading] = useState(false);
  const [areaLocations, setAreaLocations] = useState([]);
  const [moveToPicking, setMoveToPicking] = useState(false);
  const [selectedPickingLocationId, setSelectedPickingLocationId] = useState(null);
  // Storage Unit Type (for destination/picking)
  const [storageTypes, setStorageTypes] = useState([]);
  const [selectedStorageTypeId, setSelectedStorageTypeId] = useState(null);
  // New Storage Unit modal state
  const [showNewSu, setShowNewSu] = useState(false);
  const [allStorageTypes, setAllStorageTypes] = useState([]);
  const [newSuTypeId, setNewSuTypeId] = useState('');
  const [newSuNumber, setNewSuNumber] = useState('');
  const [newSuSaving, setNewSuSaving] = useState(false);

  const itemsPerCase = useMemo(() => Number(sourceRow?.qtyperunit || 0), [sourceRow]);
  const unitsOnHand = useMemo(() => Number(sourceRow?.qtyunitsonhand || 0), [sourceRow]);
  const itemsOnHand = useMemo(() => Number(sourceRow?.piecesonhand || 0), [sourceRow]);
  const singlePiecesOnHand = useMemo(
    () => Number(itemsOnHand || 0),
    [itemsOnHand]
  );

  // Max unopened storage units equals units on hand
  const maxUnopenedUnits = useMemo(() => Math.max(0, Number(unitsOnHand) || 0), [unitsOnHand]);

  const totalItemsWillMove = useMemo(() => {
    const unitEquivalent = Number(moveCases) * itemsPerCase;
    return unitEquivalent + Number(moveLooseItems || 0);
  }, [moveCases, moveLooseItems, itemsPerCase]);

  // Cap loose items so total does not exceed items on hand
  const maxLooseItems = useMemo(() => {
    const unitEquivalent = Number(moveCases) * itemsPerCase;
    return Math.max(0, Number(itemsOnHand) - unitEquivalent);
  }, [itemsOnHand, moveCases, itemsPerCase]);

  useEffect(() => {
    // Clamp current loose entry when caps change (e.g., cases updated)
    setMoveLooseItems(prev => {
      const cur = Number(prev || 0);
      return Math.max(0, Math.min(maxLooseItems, cur));
    });
  }, [maxLooseItems]);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
    }, []);

  // reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;
    setErr('');
    setLoading(false);
    // Default move quantities to on-hand amounts so the dialog reflects available units
    const perUnit = Number(sourceRow?.qtyperunit || 0);
    const uOnHand = Number(sourceRow?.qtyunitsonhand || 0);
    const pOnHand = Number(sourceRow?.piecesonhand || 0);
    let initCases = 0;
    let initLoose = 0;
    if (uOnHand > 0 && perUnit > 0) {
      initCases = uOnHand;
      initLoose = Math.max(0, pOnHand - (uOnHand * perUnit));
    } else {
      initLoose = Math.max(0, pOnHand);
    }
    setMoveCases(initCases);
    setMoveLooseItems(initLoose);
    setDestReceiptId(String(sourceRow?.receiptid || ''));
    setDestPalletOptions([]);
    setDestPalletNumber('');
    // Initialize storage unit type from source row if available
    try { setSelectedStorageTypeId(Number(sourceRow?.storageunitid || 0) || null); } catch { setSelectedStorageTypeId(null); }
  }, [isOpen, sourceRow]);

  // fetch pallets for destination receipt
  const loadDestPallets = async (rid) => {
    const id = Number(rid);
    if (!id) { setDestPalletOptions([]); return; }
    try {
      setDestLoading(true);
      const r = await fetch(`${apiBase}/api/inventory/by-receipt/${id}`);
      const json = await r.json();
      const rows = Array.isArray(json) ? json : [];
      const invPallets = rows
        .filter(row => row && row.pallet_number != null && row.pallet_number !== '')
        .map(row => ({
          pallet_number: row.pallet_number,
          type: row.storage_device || '',
          location: row.location || 'Floor',
          sku: row.sku || '',
          // Prefer explicit pallet location type when present
          locationtypeid: row.pallet_locationtypeid ?? row.locationtypeid ?? null,
        }));

      // Merge with explicit pallets (including empty pallets)
      let pal = [];
      try {
        const pr = await fetch(`${apiBase}/api/pallets/by-receipt?receiptid=${id}`);
        const pj = await pr.json();
        const list = Array.isArray(pj) ? pj : [];
        pal = list.map(p => ({
          pallet_number: p.pallet_number,
          type: p.storage_device || '',
          location: p.location || 'Floor',
          sku: '',
          locationtypeid: p.locationtypeid ?? null,
        }));
      } catch {}

      const map = new Map();
      [...invPallets, ...pal].forEach(p => {
        const key = `${String(p.pallet_number)}|${String(p.locationtypeid ?? '')}`;
        if (!map.has(key)) map.set(key, p);
      });
      const arr = Array.from(map.values());
      const sorted = arr.sort((a, b) => {
        const na = Number(a.pallet_number), nb = Number(b.pallet_number);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return String(a.pallet_number).localeCompare(String(b.pallet_number));
      });
      setDestPalletOptions(sorted);
      if (sorted.length && !destPalletNumber) {
        setDestPalletNumber(String(sorted[0].pallet_number));
        setSelectedDestRowIdx(0);
      }
    } catch (e) {
      setDestPalletOptions([]);
    } finally {
      setDestLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (destReceiptId) loadDestPallets(destReceiptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, destReceiptId]);

  // Open modal to create a new storage unit on destination WR
  const openNewStorageUnitModal = async () => {
    setErr('');
    const rid = Number(destReceiptId);
    if (!rid) { setErr('Enter a destination Receipt ID (WR#) first.'); return; }
    try {
      // Load all storage unit types
      const res = await fetch(`${apiBase}/api/storageunits`);
      const j = await res.json();
      const arr = Array.isArray(j) ? j : [];
      setAllStorageTypes(arr);
      const initialType = (selectedStorageTypeId && arr.find(t => Number(t.id) === Number(selectedStorageTypeId))) ? selectedStorageTypeId : (arr[0]?.id || '');
      setNewSuTypeId(String(initialType || ''));
    } catch {
      setAllStorageTypes([]);
      setNewSuTypeId('');
    }
    try {
      const r = await fetch(`${apiBase}/api/pallets/max-number?receiptid=${rid}`);
      const j = await r.json();
      const n = (Number(j?.maxNumber || 0) || 0) + 1;
      setNewSuNumber(String(Math.min(999, Math.max(1, n))));
    } catch {
      setNewSuNumber('1');
    }
    setShowNewSu(true);
  };

  const createNewStorageUnit = async () => {
    setErr('');
    const rid = Number(destReceiptId);
    const num = Number(newSuNumber);
    const typeId = Number(newSuTypeId);
    if (!rid) { setErr('Enter a destination Receipt ID (WR#) first.'); return; }
    if (!Number.isFinite(num) || num < 1 || num > 999) { setErr('Choose a Storage Unit # between 1 and 999.'); return; }
    if (!Number.isFinite(typeId) || typeId <= 0) { setErr('Select a Storage Unit Type.'); return; }
    try {
      setNewSuSaving(true);
      const res = await fetch(`${apiBase}/api/pallets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receiptid: rid, number: num, whstorageunitid: typeId, locationtypeid: '1' }),
      });
      const j = await res.json();
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to create storage unit');
      setShowNewSu(false);
      await loadDestPallets(rid);
      setDestPalletNumber(String(num));
      setSelectedDestRowIdx(0);
    } catch (e) {
      setErr(e?.message || 'Failed to create storage unit');
    } finally {
      setNewSuSaving(false);
    }
  };

  // Load Storage Unit Types when modal opens (picking-only)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const clientId = Number(sourceRow?.clientid || 0) || null;
        let url;
        if (clientId) {
          const qs = new URLSearchParams();
          qs.set('clientid', String(clientId));
          qs.set('pick', '1');
          url = `${apiBase}/api/clientstorageunits?${qs.toString()}`;
        } else {
          url = `${apiBase}/api/storageunits?pick=1`;
        }
        let res = await fetch(url);
        if (!res.ok && clientId) {
          // Fallback to global storageunits if client-specific endpoint fails
          res = await fetch(`${apiBase}/api/storageunits?pick=1`);
        }
        const j = await res.json();
        const arr = Array.isArray(j) ? j : [];
        setStorageTypes(arr);
        if (!selectedStorageTypeId && arr.length) setSelectedStorageTypeId(arr[0].id);
      } catch {
        setStorageTypes([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sourceRow]);

  // Load receipt meta (warehouseid) and areas/locations for the right-hand panel
  useEffect(() => {
    if (!isOpen) return;
    const rid = Number(destReceiptId || 0);
    if (!rid) {
      setDestWarehouseId(null);
      setAreaOptions([]);
      setSelectedAreaId('');
      setAreaLocations([]);
      setSelectedPickingLocationId(null);
      return;
    }
    (async () => {
      try {
        // Fetch receipt to resolve warehouseid
        const r = await fetch(`${apiBase}/api/receipts/${rid}`);
        const j = await r.json();
        const whid = Number(j?.warehouseid || sourceRow?.warehouseid || 0) || null;
        setDestWarehouseId(whid);
        if (!whid) { setAreaOptions([]); setAreaLocations([]); return; }
        // Load areas for this warehouse
        const ra = await fetch(`${apiBase}/api/warehouseareas/warehouseareas?warehouseid=${whid}&picking=1`);
        const areas = await ra.json();
        const opts = Array.isArray(areas) ? areas : [];
        setAreaOptions(opts);
        const firstAreaId = String(opts?.[0]?.id || '');
        setSelectedAreaId(prev => prev || firstAreaId);
      } catch {
        setAreaOptions([]);
        setAreaLocations([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, destReceiptId]);

  // Load locations when area/warehouse changes
  useEffect(() => {
    if (!isOpen || !moveToPicking) { setAreaLocations([]); setSelectedPickingLocationId(null); return; }
    const whid = Number(destWarehouseId || 0);
    const aid = String(selectedAreaId || '');
    if (!whid || !aid) { setAreaLocations([]); return; }
    (async () => {
      try {
        setAreaLocsLoading(true);
        const res = await fetch(`${apiBase}/api/locations?areaid=${encodeURIComponent(aid)}&warehouseid=${encodeURIComponent(whid)}&free=1`);
        const rows = await res.json();
        const arr = Array.isArray(rows) ? rows : [];
        setAreaLocations(arr);
        // Reset selection if it no longer exists in the new list
        if (!arr.some(l => Number(l.locationid) === Number(selectedPickingLocationId))) {
          setSelectedPickingLocationId(null);
        }
      } catch {
        setAreaLocations([]);
      } finally {
        setAreaLocsLoading(false);
      }
    })();
  }, [isOpen, moveToPicking, destWarehouseId, selectedAreaId, apiBase]);

  const renderLocLabel = (loc) => {
    try {
      const parts = [];
      if (loc?.roww != null) parts.push(`A${loc.roww}`);
      if (loc?.columnn != null) parts.push(`B${loc.columnn}`);
      if (loc?.shelve != null) parts.push(`S${loc.shelve}`);
      if (loc?.bin != null) parts.push(`Slot${loc.bin}`);
      return parts.length ? parts.join('-') : (loc?.type === 1 ? 'Floor' : '');
    } catch { return ''; }
  };

  // Enable Consolidate when:
  //  - destination WR and pallet are set, and
  //  - total to move > 0, and
  //  - if moving unopened units, items-per-unit must be known (>0)
  const canConsolidate = useMemo(() => {
    const destOk = String(destReceiptId || '').trim() !== '' && String(destPalletNumber || '').trim() !== '';
    const totalOk = Number(totalItemsWillMove || 0) > 0;
    const perUnitOk = Number(moveCases || 0) === 0 || Number(itemsPerCase || 0) > 0;
    return destOk && totalOk && perUnitOk;
  }, [destReceiptId, destPalletNumber, totalItemsWillMove, moveCases, itemsPerCase]);

  // Quality-of-life: if user selects a destination and hasn't entered any qty,
  // default to moving all loose items on hand to enable the action quickly.
  useEffect(() => {
    if (!isOpen) return;
    const hasDest = String(destReceiptId || '').trim() !== '' && String(destPalletNumber || '').trim() !== '';
    const noQtyYet = Number(moveCases || 0) === 0 && Number(moveLooseItems || 0) === 0;
    if (hasDest && noQtyYet && Number(itemsOnHand || 0) > 0) {
      setMoveLooseItems(Math.max(0, Number(itemsOnHand || 0)));
    }
  }, [isOpen, destReceiptId, destPalletNumber, itemsOnHand, moveCases, moveLooseItems]);

  const validate = () => {
    if (!sourceRow?.inventoryid) {
      setErr('Missing source inventory row.');
      return false;
    }
    // Only require items-per-case when moving unopened storage units
    if (Number(moveCases) > 0 && (!itemsPerCase || itemsPerCase <= 0)) {
      setErr('Qty Items Per (items-per-unit) is required when moving storage units. Enter only loose items or set Qty Items Per.');
      return false;
    }
    const total = totalItemsWillMove;
    if (total <= 0) {
      setErr('Enter a quantity to move.');
      return false;
    }
    if (total > itemsOnHand) {
      setErr(`Cannot move more than items on hand (${itemsOnHand}).`);
      return false;
    }
    if (!destReceiptId) {
      setErr('Enter a destination Receipt ID.');
      return false;
    }
    if (!destPalletNumber) {
      setErr('Choose a destination Pallet #.');
      return false;
    }
    return true;
  };

  const handleConsolidate = async () => {
    setErr('');
    if (!validate()) return;
    try {
      setLoading(true);
      // Backend to implement:
      const payload = {
        source_inventoryid: Number(sourceRow.inventoryid),
        source_receiptid: Number(sourceRow.receiptid),
        clientid: Number(sourceRow.clientid || 0),
        items_per_unit: itemsPerCase,
        move_cases: Number(moveCases || 0),
        move_opened_cases: 0,
        move_loose_items: Number(moveLooseItems || 0),
        dest_receiptid: Number(destReceiptId),
        dest_pallet_number: Number(destPalletNumber),
        username: user.fullname,
        userid: user.userid, // ← make sure this is sent
      };
      const r = await fetch(`${apiBase}/api/inventory/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok || j.success === false) {
        throw new Error(j.error || `Move failed (${r.status})`);
      }
      if (typeof onMoved === 'function') onMoved();
      onClose();
    } catch (e) {
      setErr(e.message || 'Move failed.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="modal-backdrop">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="moveInvTitle"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="modal-header" onMouseDown={startDragging} style={{ cursor: 'move' }}>
          <h3 id="moveInvTitle">Consolidate / Move Inventory Item</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body" style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Source info */}
          <div className="help-section" style={{ marginTop: 0 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Source WR# {sourceRow?.receiptid} &nbsp; | &nbsp; Pallet #{sourceRow?.pallet_number}
            </div>
            <div style={{ opacity: 0.9 }}>
              SKU: <b>{sourceRow?.sku}</b> — {sourceRow?.item_description}
            </div>
            <div style={{ fontSize: 13, marginTop: 2, opacity: 0.85 }}>
              ({unitsOnHand} unit/box(s) on hand) ({itemsPerCase} items per unit/box) — ({singlePiecesOnHand}) single pieces available — ({itemsOnHand}) total items available
            </div>
          </div>

          {/* Quantity entry table */}
          <div className="help-section">
            <h4>Enter the number of units / pieces to move</h4>
            <table className="preview-table" style={{ marginTop: 6 }}>
              <thead>
                  <tr>
                    <th>Storage Units/Box(s) (un-opened)</th>
                    <th>Individual Item(s) (loose)</th>
                    <th>Total Items that will move</th>
                  </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={maxUnopenedUnits}
                      value={moveCases}
                      onChange={e => {
                        const v = Number(e.target.value);
                        const clamped = Math.max(0, Math.min(maxUnopenedUnits, Number.isFinite(v) ? v : 0));
                        setMoveCases(clamped);
                      }}
                      className="serial-edit-input"
                      style={{ width: 120 }}
                      disabled={Number(itemsPerCase || 0) <= 0}
                      title={Number(itemsPerCase || 0) > 0 ? undefined : 'Set Qty Items Per on the source item to enable unit moves.'}
                    />
                  </td>
                  
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={maxLooseItems}
                      value={moveLooseItems}
                      onChange={e => {
                        const v = Number(e.target.value);
                        const clamped = Math.max(0, Math.min(maxLooseItems, Number.isFinite(v) ? v : 0));
                        setMoveLooseItems(clamped);
                      }}
                      className="serial-edit-input"
                      style={{ width: 120 }}
                    />
                  </td>
                  <td>
                    <input
                      disabled
                      value={totalItemsWillMove}
                      className="serial-edit-input"
                      style={{ width: 160 }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Destination + Avail Picking Locations split */}
          <div
            className="help-section"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 16,
              alignItems: 'stretch',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden', // keep inner panels constrained so their own scrollbars handle overflow
            }}
          >
            <fieldset
              style={{
                border: '2px solid var(--border-color, #3e4654)',
                borderRadius: 6,
                padding: '8px 10px',
                margin: 0,
                minWidth: 0,
                minHeight: 0,
                opacity: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <legend style={{ padding: '0 6px', fontWeight: 600 }}>Destination</legend>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  WR#&nbsp;
                  <input
                    type="number"
                    min={1}
                    value={destReceiptId}
                    onChange={(e) => setDestReceiptId(e.target.value)}
                    className="serial-edit-input"
                    style={{ width: 120 }}
                  />
                </label>
                <button className="btn sm" onClick={() => loadDestPallets(destReceiptId)} title="Reload pallets">
                  <FaSyncAlt /> Refresh Pallets
                </button>
                <button className="btn sm" onClick={openNewStorageUnitModal} title="Create a new storage unit on this WR#" style={{ fontSize: '0.9rem' }}>
                  <FaPlus /> New Storage Unit
                </button>
              </div>
              <div
                className="receive-detail-table-wrapper"
                style={{ marginTop: 8, flex: 1, minHeight: 0, maxHeight: '100%', overflow: 'auto' }}
              >
                <table className="receive-detail-table" style={{ width: '100%', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Storage Unit</th>
                      <th>SKU</th>
                      <th>Type</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destLoading ? (
                      <tr><td colSpan={3}>Loading pallets…</td></tr>
                    ) : destPalletOptions.length ? (
                      destPalletOptions.map((p, idx) => {
                        const isSelectedPallet = String(destPalletNumber) === String(p.pallet_number);
                        const isPrimary = Number(selectedDestRowIdx) === Number(idx);
                        return (
                          <tr
                            key={`${p.pallet_number}-${idx}`}
                            onClick={() => { setDestPalletNumber(String(p.pallet_number)); setSelectedDestRowIdx(idx); }}
                            aria-selected={isPrimary}
                            style={{
                              cursor: 'pointer',
                              background: isSelectedPallet ? 'rgba(255,98,0,0.14)' : undefined,
                              outline: isPrimary ? '2px solid #ff6200' : '2px solid transparent',
                              outlineOffset: '-2px'
                            }}
                          >
                            <td style={{ whiteSpace: 'nowrap' }}>{p.pallet_number}</td>
                            <td className="nowrap">{p.sku || ''}</td>
                            <td className="nowrap">{p.type || ''}</td>
                            <td className="nowrap">{p.location || 'Floor'}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan={3}>No pallets found for this receipt.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </fieldset>

            {false && (
            <fieldset
              style={{
                border: '2px solid var(--border-color, #3e4654)',
                borderRadius: 6,
                padding: '8px 10px',
                margin: 0,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <legend style={{ padding: '0 6px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0 }}>
                    <input type="checkbox" checked={moveToPicking} onChange={e => setMoveToPicking(e.target.checked)} />
                    <span>Move to Picking</span>
                  </label>
                  <strong style={{ marginLeft: 10 }}>Avail Picking Locations</strong>
                </span>
              </legend>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, marginBottom: 8, opacity: moveToPicking ? 1 : 0.6, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Area:&nbsp;
                  <select
                    value={selectedAreaId}
                    onChange={(e) => setSelectedAreaId(e.target.value)}
                    className="serial-edit-input"
                    style={{ minWidth: 200 }}
                    disabled={!destWarehouseId || !moveToPicking}
                  >
                    {areaOptions.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Storage Unit Type:&nbsp;
                  <select
                    value={selectedStorageTypeId ?? ''}
                    onChange={(e) => setSelectedStorageTypeId(Number(e.target.value) || null)}
                    className="serial-edit-input"
                    style={{ minWidth: 200 }}
                    disabled={!moveToPicking}
                  >
                    {storageTypes.map(st => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div
                className="receive-detail-table-wrapper"
                style={{
                  marginTop: 8,
                  flex: 1,
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'auto',
                  opacity: moveToPicking ? 1 : 0.5,
                  pointerEvents: moveToPicking ? 'auto' : 'none'
                }}
              >
                <table className="receive-detail-table" style={{ width: '100%', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Column</th>
                      <th>Shelf</th>
                      <th>Bin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaLocsLoading ? (
                      <tr><td colSpan={4}>Loading locations…</td></tr>
                    ) : areaLocations.length ? (
                      areaLocations.map((loc) => (
                        <tr
                          key={loc.locationid}
                          onClick={() => setSelectedPickingLocationId(Number(loc.locationid))}
                          style={{
                            cursor: 'pointer',
                            background: Number(selectedPickingLocationId) === Number(loc.locationid)
                              ? 'rgba(255,98,0,0.18)'
                              : undefined
                          }}
                        >
                          <td>{loc.roww ?? ''}</td>
                          <td>{loc.columnn ?? ''}</td>
                          <td>{loc.shelve ?? ''}</td>
                          <td>{loc.bin ?? ''}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={4}>No locations for this area.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </fieldset>
            )}
          </div>

          {err ? (
            <div className="help-section" style={{ color: '#ff6b6b', fontWeight: 600 }}>
              {err}
            </div>
          ) : null}
        </div>

        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button className="btn secondary" onClick={onClose}>
            <FaTimes style={{ marginRight: 6 }} /> Cancel
          </button>
          <button className="btn primary" onClick={handleConsolidate} disabled={loading || !canConsolidate}>
            {loading ? 'Working…' : (<><FaCheck style={{ marginRight: 6 }} /> Consolidate</>)}
          </button>
        </div>
        <div
          className="moveinv-resize"
          onMouseDown={startResizing}
          title="Resize"
          style={{ position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'nwse-resize',
                   background: 'linear-gradient(135deg, transparent 50%, var(--primary-color, #0ea5e9) 50%)' }}
        />
      </div>
    </div>
    {showNewSu && (
      <div className="modal-overlay" style={{ zIndex: 20000 }} onMouseDown={(e)=>{ if (e.target===e.currentTarget) setShowNewSu(false); }}>
        <div className="modal" role="dialog" aria-modal="true" aria-label="New Storage Unit" onMouseDown={(e)=>e.stopPropagation()}>
          <h2 style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>New Storage Unit on WR #{String(destReceiptId || '')}</span>
            <button className="mini-btn" onClick={()=>setShowNewSu(false)} title="Close"><FaTimes/></button>
          </h2>
          <div className="modal-scroll">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0,1fr))', gap:'8px 12px', marginBottom: 8 }}>
              <label style={{ display:'flex', flexDirection:'column', gap: 4 }}>
                <span style={{ fontSize: '.9rem', opacity:.85 }}>Storage Unit Type</span>
                <select value={newSuTypeId} onChange={e=>setNewSuTypeId(e.target.value)}>
                  <option value="">-- Select --</option>
                  {allStorageTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              <label style={{ display:'flex', flexDirection:'column', gap: 4 }}>
                <span style={{ fontSize: '.9rem', opacity:.85 }}>Storage Unit #</span>
                <select value={newSuNumber} onChange={e=>setNewSuNumber(e.target.value)}>
                  {Array.from({ length: 999 }, (_, i) => String(i+1)).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <div style={{ fontSize: '.9rem', opacity:.8 }}>Creates an empty storage unit on the destination WR# so you can consolidate items into it.</div>
          </div>
          <div className="modal-actions" style={{ display:'flex', justifyContent:'flex-end', gap: 8 }}>
            <button className="btn" onClick={()=>setShowNewSu(false)}>Cancel</button>
            <button className="btn primary" disabled={newSuSaving} onClick={createNewStorageUnit}>{newSuSaving ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
